import Color from "color";

// Types only: the module is used while orders are mapped
// (services/Order → cells), so value imports would make a cycle.
import type { OrderPaymentStatus } from "../redux/constants/OrdersPage/order";
import type Order from "../services/Order";

/**
 * One payment status per order for the "Payment status" column and the row
 * tint: the payment link / QR state when the order has a link, otherwise the
 * card payment stage (hold at accept, capture or refund at close).
 */
export const ORDER_PAYMENT_STATUS_KEYS = [
	"holdPending",
	"holdSuccess",
	"holdFailed",
	"paymentPending",
	"paymentSuccess",
	"paymentPart",
	"paymentFailed",
	"refundPending",
	"refundSuccess",
	"refundFailed",
	"linkPending",
	"linkFailed",
	"linkPaid",
] as const;

export type OrderPaymentStatusKey = (typeof ORDER_PAYMENT_STATUS_KEYS)[number];

/** Key of the status color in `state.settings.colors`. */
export type OrderPaymentStatusColorKey =
	`paymentStatus${Capitalize<OrderPaymentStatusKey>}`;

export const paymentStatusColorKey = (
	key: OrderPaymentStatusKey,
): OrderPaymentStatusColorKey =>
	`paymentStatus${key.charAt(0).toUpperCase()}${key.slice(
		1,
	)}` as OrderPaymentStatusColorKey;

/** Server settings `ui.colors.orders.paymentStatus` → color slice keys. */
export const paymentStatusColors = (
	stored: Partial<Record<OrderPaymentStatusKey, string>> | null | undefined,
): Partial<Record<OrderPaymentStatusColorKey, string>> =>
	Object.fromEntries(
		ORDER_PAYMENT_STATUS_KEYS.filter((key) => stored?.[key]).map((key) => [
			paymentStatusColorKey(key),
			stored?.[key] as string,
		]),
	);

/**
 * Text on a status fill: white on saturated colors, as in the other status
 * cells; dark only on really light colors picked in settings.
 */
export const paymentStatusTextColor = (
	backgroundColor: string | undefined,
): string | undefined => {
	if (!backgroundColor) return undefined;
	try {
		return Color(backgroundColor).luminosity() > 0.6
			? "#000000"
			: "#FFFFFF";
	} catch {
		return undefined;
	}
};

/** Statuses for which the whole order row is tinted: money is expected now. */
export const ROW_TINT_PAYMENT_STATUSES: readonly OrderPaymentStatusKey[] = [
	"paymentPending",
	"linkPending",
];

/** Card payment stage (`orderPrepared.paymentStatus.status`) → status. */
const CARD_STATUS_KEYS: Partial<
	Record<`${OrderPaymentStatus}`, OrderPaymentStatusKey>
> = {
	card_validation: "holdPending",
	card_validation_success: "holdSuccess",
	card_validation_failed: "holdFailed",
	payment_pending: "paymentPending",
	payment_success: "paymentSuccess",
	payment_part: "paymentPart",
	payment_failed: "paymentFailed",
	refund_pending: "refundPending",
	refund_success: "refundSuccess",
	refund_failed: "refundFailed",
};

type OrderLike =
	| Pick<
			Partial<Order.Model>,
			"additionalFields" | "paymentStatus" | "paymentType"
	  >
	| null
	| undefined;

const linkStatus = (
	qrPayment: Order.QrPayment | null | undefined,
): OrderPaymentStatusKey | null => {
	switch (qrPayment?.status) {
		case "paid":
			return "linkPaid";
		case "failed":
			return "linkFailed";
		case "pending": {
			// A declined card keeps the link payable (status stays pending):
			// it is a failure until the link is sent again.
			const sends = qrPayment.smsSends ?? [];
			const issuedAt = Date.parse(
				sends[sends.length - 1]?.sentAt ?? qrPayment.createdAt ?? "",
			);
			const failedAt = Date.parse(qrPayment.lastFailedAt ?? "");
			return failedAt > (Number.isNaN(issuedAt) ? 0 : issuedAt)
				? "linkFailed"
				: "linkPending";
		}
		default:
			// A cancelled link (the client paid cash) has no status of its own.
			return null;
	}
};

export const getOrderPaymentStatus = (
	order: OrderLike,
): OrderPaymentStatusKey | null => {
	if (!order) return null;

	const byLink = linkStatus(order.additionalFields?.qrPayment);
	if (byLink) return byLink;

	if (String(order.paymentType) !== "card") return null;

	const status = order.paymentStatus?.status;
	return status ? CARD_STATUS_KEYS[status] ?? null : null;
};

type OrderWithQrPayment =
	| { additionalFields?: Order.IAdditionalFields }
	| null
	| undefined;

/** The link sent by SMS is waiting for the payment. */
export const isPaymentLinkSmsPending = (order: OrderWithQrPayment): boolean => {
	const qrPayment = order?.additionalFields?.qrPayment;
	return (
		Boolean(qrPayment?.smsSends?.length) && qrPayment?.status === "pending"
	);
};

/**
 * The link sent by SMS is still waiting and the client has not failed to pay
 * since the last send: sending it again is not allowed. A declined card keeps
 * the link payable (status stays pending), so after a failed attempt the
 * dispatcher may send it again; a cancelled link is replaced by a new one.
 */
export const isPaymentLinkSmsAwaiting = (
	order: OrderWithQrPayment,
): boolean => {
	if (!isPaymentLinkSmsPending(order)) return false;

	const qrPayment = order?.additionalFields?.qrPayment;
	const sends = qrPayment?.smsSends ?? [];
	const lastSentAt = Date.parse(sends[sends.length - 1]?.sentAt ?? "");
	const lastFailedAt = Date.parse(qrPayment?.lastFailedAt ?? "");

	return !(lastFailedAt > lastSentAt);
};
